<template>
  <footer class="footer-wrapper">
    <div class="footer-inner">
      <div class="footer-content">
        <!-- LEFT: BRAND + ABOUT -->
        <div class="footer-block footer-block--brand">
          <h3 class="footer-title">IpromiseAfrica</h3>
          <p class="footer-text">
            Your sports ecosystem — gear, resale, facilities, tournaments, matches, talent pathways,
            and inclusive sport across Kenya and beyond.
          </p>
          <div class="country-info">
            <img
              :src="kenyaFlagSrc"
              alt="Kenya Flag"
              class="kenya-flag"
              loading="lazy"
              decoding="async"
              width="48"
              height="32"
            />
            <div class="country-details">
              <span class="country-name">Republic of Kenya</span>
              <span class="country-number">Country code +254</span>
            </div>
          </div>
        </div>

        <!-- CENTER: PRODUCTS & SERVICES -->
        <div class="footer-block footer-block--links">
          <h3 class="footer-title">Products &amp; Services</h3>
          <ul class="footer-links footer-links--grid">
            <li v-for="link in productLinks" :key="link.to">
              <router-link :to="link.to">{{ link.label }}</router-link>
            </li>
          </ul>
          <div class="footer-partner-block">
            <h4 class="footer-subtitle">Hosts &amp; sellers</h4>
            <ul class="footer-links footer-links--compact">
              <li v-for="link in partnerLinks" :key="link.to">
                <router-link :to="link.to">{{ link.label }}</router-link>
              </li>
            </ul>
          </div>
        </div>

        <!-- RIGHT: CONTACT & SOCIAL -->
        <div class="footer-block footer-block--contact">
          <h3 class="footer-title">Connect With Us</h3>
          <div class="contact-info">
            <a class="contact-item" href="mailto:info@ipromiseafrica.com">
              <i class="fa-solid fa-envelope" aria-hidden="true"></i>
              <span>info@ipromiseafrica.com</span>
            </a>
            <a class="contact-item" href="tel:+254791869346">
              <i class="fa-solid fa-phone" aria-hidden="true"></i>
              <span>+254 791 869 346</span>
            </a>
            <div class="contact-item contact-item--static">
              <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
              <span>Nairobi, Kenya</span>
            </div>
          </div>
          <div class="social-icons" aria-label="Social media">
            <a
              class="social-link"
              href="https://x.com/IpromiseAfrica"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="IpromiseAfrica on X (opens in a new tab)"
            >
              <i class="fa-brands fa-x-twitter" aria-hidden="true"></i>
            </a>
            <a
              class="social-link"
              href="https://www.instagram.com/ipromiseafrica/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="IpromiseAfrica on Instagram (opens in a new tab)"
            >
              <i class="fa-brands fa-instagram" aria-hidden="true"></i>
            </a>
            <a
              class="social-link"
              href="https://www.linkedin.com/in/ipromise-africa-8b692a401/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="IpromiseAfrica on LinkedIn (opens in a new tab)"
            >
              <i class="fa-brands fa-linkedin-in" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- BOTTOM: COPYRIGHT & LEGAL -->
      <div class="footer-bottom">
        <div class="copyright-section">
          <p class="copyright-text">
            Copyright © {{ new Date().getFullYear() }} IpromiseAfrica Inc. All rights reserved.
          </p>
          <div class="legal-links">
            <router-link to="/ipromiseafrica/legal/internet-services/privacy-policy" class="legal-link">Privacy Policy</router-link>
            <span class="divider">•</span>
            <router-link to="/ipromiseafrica/legal/internet-services/terms-of-use" class="legal-link">Terms of Use</router-link>
            <span class="divider">•</span>
            <router-link to="/ipromiseafrica/store/return-policy" class="legal-link">Sales and Refunds</router-link>
            <span class="divider">•</span>
            <span class="legal-link legal-link--disabled" aria-disabled="true">Site Map</span>
          </div>
        </div>

        <div class="footer-credits">
          <p>Developed and maintained by <span class="highlight">Tech Assassin</span>.</p>
        </div>
      </div>
    </div>

    <button type="button" class="back-to-top" @click="scrollToTop" :class="{ visible: showBackToTop }" aria-label="Back to top">
      <i class="fa-solid fa-chevron-up" aria-hidden="true"></i>
    </button>
  </footer>
</template>

<script>
import kenyaFlagSrc from "../../../assets/images/landing/kenya.png";

export default {
  name: "MainFooter",
  data() {
    return {
      showBackToTop: false,
      kenyaFlagSrc,
      productLinks: [
        { to: "/ipromiseafrica/store", label: "IPA Store — sports gear" },
        { to: "/ipromiseafrica/resale", label: "Resale Store" },
        { to: "/ipromiseafrica/facility", label: "Book sports facilities" },
        { to: "/ipromiseafrica/facility/installation", label: "Facility installation" },
        { to: "/ipromiseafrica/tournament", label: "Tournaments & prize money" },
        { to: "/ipromiseafrica/matchup", label: "Matches & team challenges" },
        { to: "/ipromiseafrica/matchup/league", label: "IpromiseLeague" },
        { to: "/ipromiseafrica/pathfinder", label: "Pathfinder" },
        { to: "/ipromiseafrica/the-able", label: "The Able" },
      ],
      partnerLinks: [
        { to: "/ipromiseafrica/facility/owner-registration", label: "List your facility" },
        { to: "/ipromiseafrica/resale/seller-sign-up", label: "Sell on resale marketplace" },
        { to: "/ipromiseafrica/matchup/officials/register", label: "Register as match official" },
      ],
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    },
  },
};
</script>

<style scoped>
.footer-wrapper {
  width: 100%;
  background: var(--background-color2);
  color: var(--text-color1);
  padding: 3rem clamp(1rem, 4vw, 3rem) 1.75rem;
  font-family: "Poppins", sans-serif;
  position: relative;
  margin-top: 0;
  border-top: 1px solid var(--border-color-hover1, #e5e7eb);
}

.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
}

.footer-content {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.35fr) minmax(0, 0.95fr);
  gap: clamp(1.5rem, 3vw, 2.75rem);
  align-items: start;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid color-mix(in srgb, var(--text-color1) 12%, transparent);
}

.footer-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.footer-title {
  color: var(--text-color1);
  font-size: 1rem;
  margin: 0 0 1rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 0.55rem;
  width: 100%;
}

.footer-title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 2.5rem;
  height: 2px;
  background: var(--border-color-hover, gold);
  border-radius: 2px;
}

.footer-text {
  color: var(--text-color2);
  line-height: 1.6;
  font-size: 0.84rem;
  margin: 0 0 1.25rem;
  max-width: 34ch;
}

.country-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;
  padding: 0.65rem 0.85rem;
  background: color-mix(in srgb, var(--background-color1) 50%, var(--background-color2));
  border-radius: 10px;
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  width: 100%;
  max-width: 280px;
}

.kenya-flag {
  width: 36px;
  height: 24px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
}

.country-details {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.country-name {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--text-color1);
}

.country-number {
  font-size: 0.75rem;
  color: var(--text-color2);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.footer-subtitle {
  margin: 0 0 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-color2);
}

.footer-partner-block {
  margin-top: 1.25rem;
  padding-top: 1.1rem;
  border-top: 1px solid color-mix(in srgb, var(--text-color1) 10%, transparent);
  width: 100%;
}

.footer-links--grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 1.5rem;
  row-gap: 0.35rem;
}

.footer-links--compact {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.footer-links li {
  margin: 0;
}

.footer-links a {
  display: block;
  font-size: 0.84rem;
  line-height: 1.45;
  padding: 0.2rem 0;
  color: var(--text-color1);
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

.footer-links a:hover {
  color: var(--border-color-hover, var(--primary-color));
  transform: translateX(3px);
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-bottom: 1.25rem;
  width: 100%;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.84rem;
  color: var(--text-color1);
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 1.4;
}

.contact-item:hover {
  color: var(--border-color-hover, var(--primary-color));
}

.contact-item--static {
  cursor: default;
}

.contact-item--static:hover {
  color: var(--text-color1);
}

.contact-item i {
  color: var(--border-color-hover, var(--primary-color));
  width: 1rem;
  font-size: 0.9rem;
  flex-shrink: 0;
  text-align: center;
}

.social-icons {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.social-link {
  font-size: 1rem;
  color: var(--text-color1);
  transition: all 0.25s ease;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--background-color1) 45%, var(--background-color2));
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  border-radius: 50%;
  text-decoration: none;
}

.social-link:hover {
  color: var(--border-color-hover, var(--primary-color));
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
}

.footer-bottom {
  text-align: center;
}

.copyright-text {
  color: var(--text-color2);
  font-size: 0.82rem;
  margin: 0 0 0.75rem;
}

.legal-links {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
}

.legal-link {
  color: var(--text-color2);
  text-decoration: none;
  font-size: 0.78rem;
  transition: color 0.2s ease;
}

.legal-link:hover {
  color: var(--border-color-hover, var(--primary-color));
}

.legal-link--disabled {
  cursor: default;
  pointer-events: none;
  opacity: 0.55;
}

.legal-link--disabled:hover {
  color: var(--text-color2);
}

.divider {
  color: var(--text-color2);
  opacity: 0.45;
  font-size: 0.7rem;
}

.footer-credits {
  color: var(--text-color2);
  font-size: 0.78rem;
  line-height: 1.5;
  margin-top: 1rem;
}

.highlight {
  color: var(--border-color-hover, var(--primary-color));
  font-weight: 600;
}

.back-to-top {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 45px;
  height: 45px;
  background: var(--border-color-hover, var(--primary-color));
  color: var(--text-color3);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.back-to-top.visible {
  opacity: 1;
  visibility: visible;
}

.back-to-top:hover {
  transform: translateY(-3px);
}

@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: 1fr 1fr;
  }

  .footer-block--contact {
    grid-column: 1 / -1;
  }

  .footer-text {
    max-width: none;
  }

  .country-info {
    max-width: none;
  }
}

@media (max-width: 720px) {
  .footer-wrapper {
    padding: 2.5rem 1.25rem 1.5rem;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .footer-block {
    align-items: center;
  }

  .footer-title::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .footer-text {
    max-width: 36ch;
  }

  .footer-links--grid {
    grid-template-columns: 1fr;
    max-width: 320px;
    text-align: left;
  }

  .footer-partner-block {
    max-width: 320px;
    text-align: left;
  }

  .footer-subtitle {
    text-align: left;
  }

  .country-info {
    justify-content: center;
    max-width: 300px;
  }

  .contact-info {
    align-items: center;
  }

  .contact-item,
  .contact-item--static {
    justify-content: center;
  }

  .social-icons {
    justify-content: center;
  }

  .footer-links a:hover {
    transform: none;
  }

  .back-to-top {
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .divider {
    display: none;
  }

  .legal-link {
    font-size: 0.75rem;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.footer-wrapper {
  animation: fadeInUp 0.5s ease;
}
</style>
